import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { format, subDays } from "date-fns";
import {
    Loader2,
    Plus,
    FileDown,
    FileUp,
    Receipt,
    Printer,
    Download,
    X,
    Wallet,
    ArrowUpCircle,
    CreditCard,
    Calendar,
    Search
} from "lucide-react";
import { DataTable } from "../components/ui/DataTable";
import { fetchReceiptsForStaff } from "../apiHelper";
import { useToast } from "../hooks/useToast";

const Receipts = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();

    const [user, setUser] = useState(null);
    const [rawReceipts, setRawReceipts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    const [startDt, setStartDt] = useState(() => {
        const oneDayAgo = subDays(new Date(), 0);
        return format(oneDayAgo, "yyyy-MM-dd");
    });

    const [endDt, setEndDt] = useState(() =>
        format(new Date(), "yyyy-MM-dd")
    );

    const [showActions, setShowActions] = useState(false);

    // 1. FILTER LOGIC: Strict exclusion of non-cash/bank movement
    const receipts = useMemo(() => {
        let filtered = rawReceipts.filter(r => Number(r.summary_collected) > 0);

        if (searchTerm) {
            filtered = filtered.filter(r =>
                r.idempotency_key?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                r.customer?.user?.first_name?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        return filtered;
    }, [rawReceipts, searchTerm]);

    // 2. FINANCIAL SUMMARY: Calculating based on filtered (collected) data
    const totals = useMemo(() => {
        return receipts.reduce((acc, curr) => {
            acc.collected += Number(curr.summary_collected || 0);
            acc.credit += Number(curr.summary_credit || 0);
            acc.gross += Number(curr.totalproduct || 0);
            return acc;
        }, { collected: 0, credit: 0, gross: 0 });
    }, [receipts]);

    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            navigate("/login");
            return;
        }
        setUser(JSON.parse(lcUser));
    }, [navigate]);

    useEffect(() => {
        if (!user?.users_id) return;

        const loadReceipts = async () => {
            setLoading(true);
            try {
                const data = await fetchReceiptsForStaff({
                    users_id: user.users_id,
                    start_dt: startDt,
                    end_dt: endDt,
                });
                setRawReceipts(Array.isArray(data) ? data : []);
            } catch (err) {
                showToast({ message: "Failed to load receipts.", show: true, toastType: 2 });
            } finally {
                setLoading(false);
            }
        };

        loadReceipts();
    }, [user, startDt, endDt, showToast]);

    const formatCurrency = (amount) =>
        new Intl.NumberFormat("en-NG", {
            style: "currency",
            currency: "NGN",
        }).format(amount || 0);

    const getStatusBadge = (status) => {
        switch (status) {
            case "OUT": return { label: "Fully Paid", color: "bg-green-100 text-green-800" };
            case "HLD": return { label: "Split/Deposit", color: "bg-blue-100 text-blue-800" };
            default: return { label: status, color: "bg-gray-100 text-gray-700" };
        }
    };

    const columns = [
        {
            key: "placed_on",
            label: "Date & Time",
            render: (v) => <span className="text-xs font-medium">{format(new Date(v), "d MMM, hh:mm a")}</span>,
        },
        {
            key: "idempotency_key",
            label: "Receipt #",
            render: (v, r) => (
                <span className="font-mono text-[11px] font-bold text-blue-600">
                    {v?.slice(-10).toUpperCase() || `REC-${r.orders_id}`}
                </span>
            ),
        },
        {
            key: "customer",
            label: "Customer",
            render: (_, r) => {
                const u = r.customer?.user;
                return <span className="text-sm font-semibold">{u ? `${u.first_name} ${u.last_name}` : "Walk-in"}</span>;
            },
        },
        {
            key: "summary_collected",
            label: "Paid Today",
            render: (v) => <span className="font-bold text-green-700">{formatCurrency(v)}</span>,
        },
        {
            key: "summary_credit",
            label: "Balance",
            render: (v) => (
                <span className={`text-xs ${v > 0 ? "text-red-500 font-bold" : "text-gray-400"}`}>
                    {v > 0 ? formatCurrency(v) : "Cleared"}
                </span>
            ),
        },
        {
            key: "status",
            label: "Status",
            render: (status) => {
                const { label, color } = getStatusBadge(status);
                return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${color}`}>{label}</span>;
            },
        },
    ];

    const handleRowAction = (actionKey, row) => {
        if (actionKey === "view") {
            navigate("/sales/receipts/view", { state: { selectedReceipt: row, allReceipts: receipts } });
        } else {
            showToast({ message: "Action initiated...", show: true, toastType: 1 });
        }
    };

    if (!user) return null;

    return (
        <div className="min-h-screen bg-gray-50 pb-20">
            <div className="w-full mx-auto pt-6 px-4 sm:px-6">

                {/* 1. HEADER SECTION */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                    <div>
                        <h2 className="text-2xl font-black text-gray-900 flex items-center gap-2">
                            <Receipt className="text-blue-600" size={28} />
                            Collection Journal
                        </h2>
                        <p className="text-xs text-gray-500 font-medium">Monitoring actual cash & bank inflows</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                            <input
                                type="text"
                                placeholder="Search receipts..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none w-64"
                            />
                        </div>
                    </div>
                </div>

                {/* 2. SUMMARY CARDS */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                    <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm flex items-center gap-4 transition-transform hover:scale-[1.02]">
                        <div className="p-3 bg-green-100 text-green-600 rounded-xl"><Wallet size={24} /></div>
                        <div>
                            <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">Total Collected</p>
                            <h3 className="text-2xl font-black text-gray-900">{formatCurrency(totals.collected)}</h3>
                        </div>
                    </div>
                    <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm flex items-center gap-4 transition-transform hover:scale-[1.02]">
                        <div className="p-3 bg-red-50 text-red-500 rounded-xl"><CreditCard size={24} /></div>
                        <div>
                            <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">New Debt</p>
                            <h3 className="text-2xl font-black text-gray-900">{formatCurrency(totals.credit)}</h3>
                        </div>
                    </div>
                    <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm flex items-center gap-4 transition-transform hover:scale-[1.02]">
                        <div className="p-3 bg-blue-50 text-blue-600 rounded-xl"><ArrowUpCircle size={24} /></div>
                        <div>
                            <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">Total Sales</p>
                            <h3 className="text-2xl font-black text-gray-900">{formatCurrency(totals.gross)}</h3>
                        </div>
                    </div>
                </div>

                {/* 3. FILTERS */}
                <div className="mb-6 bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex flex-wrap gap-6 items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <Calendar size={16} className="text-gray-400" />
                            <input
                                type="date"
                                value={startDt}
                                onChange={(e) => setStartDt(e.target.value)}
                                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                            />
                        </div>
                        <span className="text-gray-300 font-bold">to</span>
                        <input
                            type="date"
                            value={endDt}
                            onChange={(e) => setEndDt(e.target.value)}
                            className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                    </div>
                    <button
                        onClick={() => {
                            setStartDt(format(subDays(new Date(), 1), "yyyy-MM-dd"));
                            setEndDt(format(new Date(), "yyyy-MM-dd"));
                        }}
                        className="text-xs font-bold text-blue-600 hover:text-blue-700 underline decoration-2 underline-offset-4"
                    >
                        Reset to Last 24h
                    </button>
                </div>

                {/* 4. DATA TABLE */}
                <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-32 gap-4">
                            <Loader2 className="animate-spin text-blue-600" size={48} />
                            <p className="text-sm text-gray-400 font-bold animate-pulse">Filtering Financial Records...</p>
                        </div>
                    ) : (
                        <DataTable
                            columns={columns}
                            data={receipts}
                            title=""
                            rowActions={[{ key: "view", label: "View Details" }, { key: "print", label: "Print Thermal", icon: Printer }]}
                            onAction={handleRowAction}
                            classNames="border-none"
                        />
                    )}
                </div>
            </div>

            {/* 5. FLOATING ACTIONS */}
            <div className="fixed bottom-8 right-8 z-50">
                <button
                    onClick={() => setShowActions(!showActions)}
                    className={`w-16 h-16 rounded-full shadow-2xl flex items-center justify-center text-white transition-all transform active:scale-90 ${showActions ? "bg-gray-900 rotate-45" : "bg-blue-600 hover:bg-blue-700"}`}
                >
                    {showActions ? <X size={32} /> : <Plus size={32} />}
                </button>

                {showActions && (
                    <div className="absolute bottom-20 right-0 flex flex-col gap-3">
                        <button onClick={() => navigate("/sales/receipts/new")} className="flex items-center gap-3 bg-white px-4 py-3 rounded-xl shadow-xl border border-gray-100 hover:bg-gray-50">
                            <span className="text-sm font-bold text-gray-700">New Receipt</span>
                            <div className="p-2 bg-blue-100 text-blue-600 rounded-lg"><Plus size={18} /></div>
                        </button>
                        <button className="flex items-center gap-3 bg-white px-4 py-3 rounded-xl shadow-xl border border-gray-100 hover:bg-gray-50">
                            <span className="text-sm font-bold text-gray-700">Export CSV</span>
                            <div className="p-2 bg-gray-100 text-gray-600 rounded-lg"><FileDown size={18} /></div>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Receipts;